/*
 * holy-auto-ritual.js · v9 · 2026-06-05
 *
 * Toggle + cadence state + PDP price sync.
 *
 * Behaviors:
 *   - OFF (default): selling_plan input value = "" → one-time purchase
 *   - ON: input value = currently selected plan ID
 *   - Default cadence: index 1 if available, else 0
 *   - Clicking any cadence button while OFF turns AR ON at that cadence
 *   - On any state change, Wonder's main price block (.price.price--large
 *     in the buy box) gets its main price item swapped to the active plan
 *     price (when ON) or restored to the original base price (when OFF).
 *     Compare-at price (if any) is preserved.
 *
 * Wonder DOM contract (assumed; degrades gracefully if missing):
 *   .price.price--large
 *     .price__regular .price-item--regular   ← regular state, main amount
 *     .price__sale    .price-item--sale      ← sale state, current amount
 *                     .price-item--regular   ← sale state, compare-at (strike)
 */

(function () {
  'use strict';

  function init(root) {
    if (!root || root.__holyArInit) return;
    root.__holyArInit = true;

    var input    = root.querySelector('[data-holy-ar-input]');
    var toggle   = root.querySelector('[data-holy-ar-toggle]');
    var opts     = root.querySelectorAll('[data-holy-ar-cad]');
    if (!input || !toggle || opts.length === 0) return;

    // Default cadence index
    var defaultIdx = opts.length > 1 ? 1 : 0;
    var selectedIdx = defaultIdx;
    setActiveOption(selectedIdx);

    // Wonder's main price element + cached original markup are resolved
    // LAZILY on each state change — some products/templates render the
    // price block after my mount, or Wonder swaps it on variant change.
    var priceEl = null;
    var priceCache = null;

    function ensurePriceEl() {
      var current = findPriceElement(root);
      if (current && current !== priceEl) {
        priceEl = current;
        priceCache = snapshotPrice(priceEl);
      } else if (!current) {
        priceEl = null;
        priceCache = null;
      }
      return priceEl;
    }
    ensurePriceEl();

    function setActiveOption(idx) {
      for (var i = 0; i < opts.length; i++) {
        if (i === idx) {
          opts[i].classList.add('holy-ar__cad-opt--active');
        } else {
          opts[i].classList.remove('holy-ar__cad-opt--active');
        }
      }
    }

    function activePlanPriceMoney() {
      var btn = opts[selectedIdx];
      return btn ? btn.getAttribute('data-plan-price-money') : null;
    }

    function turnOn() {
      root.classList.add('holy-ar--on');
      root.setAttribute('data-holy-ar-state', 'on');
      toggle.setAttribute('aria-pressed', 'true');
      var plan = opts[selectedIdx];
      if (plan) {
        input.value = plan.getAttribute('data-plan-id') || '';
      }
      ensurePriceEl();
      if (priceEl && priceCache) {
        applyPlanPriceToWonder(priceEl, priceCache, activePlanPriceMoney());
      }
    }

    function turnOff() {
      root.classList.remove('holy-ar--on');
      root.setAttribute('data-holy-ar-state', 'off');
      toggle.setAttribute('aria-pressed', 'false');
      input.value = '';
      ensurePriceEl();
      if (priceEl && priceCache) {
        restorePrice(priceEl, priceCache);
      }
    }

    function isOn() {
      return root.classList.contains('holy-ar--on');
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      if (isOn()) {
        turnOff();
      } else {
        turnOn();
      }
    });

    for (var i = 0; i < opts.length; i++) {
      (function (idx) {
        opts[idx].addEventListener('click', function (e) {
          e.preventDefault();
          selectedIdx = idx;
          setActiveOption(idx);
          if (!isOn()) {
            turnOn();
          } else {
            input.value = opts[idx].getAttribute('data-plan-id') || '';
            ensurePriceEl();
            if (priceEl && priceCache) {
              applyPlanPriceToWonder(priceEl, priceCache, activePlanPriceMoney());
            }
          }
        });
      })(i);
    }
  }

  // ----- Price helpers -----

  function findPriceElement(root) {
    // Walk up to the product section, then query within
    var section = root.closest('.shopify-section, section, [data-section-id], [data-section-type]') || document;
    return section.querySelector('.price.price--large')
        || section.querySelector('.price--large')
        || section.querySelector('.price');
  }

  function snapshotPrice(priceEl) {
    // Capture original markup so we can restore on OFF
    return {
      html: priceEl.innerHTML
    };
  }

  function restorePrice(priceEl, cache) {
    priceEl.innerHTML = cache.html;
  }

  function applyPlanPriceToWonder(priceEl, cache, planMoney) {
    if (planMoney == null) return;

    // Start from a fresh copy of the original markup, then mutate every
    // price-final span. Wonder's price.liquid puts the dollar amount inside
    // a span with .wt-product__price__final — present BOTH in .price__regular
    // (visible when not on sale) AND in .price__sale (visible when on sale).
    // Whichever is actually displayed gets the updated amount.
    priceEl.innerHTML = cache.html;

    var finals = priceEl.querySelectorAll('.wt-product__price__final');
    if (finals.length > 0) {
      for (var i = 0; i < finals.length; i++) {
        finals[i].textContent = planMoney;
      }
      return;
    }

    // Defensive fallback if the .wt-product__price__final class isn't found
    // (theme variants, future Wonder changes). Try the standard Shopify
    // price-item classes by visibility precedence.
    var saleSlot = priceEl.querySelector('.price__sale.visible .price-item--sale')
                || priceEl.querySelector('.price__sale.visible-main-product .price-item--sale');
    if (saleSlot) {
      saleSlot.textContent = planMoney;
      return;
    }
    var regularSlot = priceEl.querySelector('.price__regular .price-item--regular');
    if (regularSlot) {
      regularSlot.textContent = planMoney;
      return;
    }
    var anyPriceItem = priceEl.querySelector('.price-item--regular') || priceEl.querySelector('.price-item');
    if (anyPriceItem) {
      anyPriceItem.textContent = planMoney;
    }
  }

  // ----- Lifecycle -----

  function initAll() {
    var roots = document.querySelectorAll('[data-holy-ar]');
    for (var i = 0; i < roots.length; i++) {
      init(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section) return;
    var roots = section.querySelectorAll('[data-holy-ar]');
    for (var i = 0; i < roots.length; i++) {
      init(roots[i]);
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    if (!block) return;
    var root = block.querySelector('[data-holy-ar]')
            || (block.matches && block.matches('[data-holy-ar]') ? block : null);
    if (root) init(root);
  });
})();
